import { useVideoMetadataMutation } from "@/hooks/assets";
import RadioButtonGroup from "@ingka/radio-button-group";
import Text from "@ingka/text";
import { UpdateRelatedAssetMetadataDtoAccessibilityPurposeEnum } from "generated";
import { useShallow } from "zustand/react/shallow";
import { Question } from "./Question";
import { useStore } from "@/services/store";

type Props = {
  assetId: number;
  isLockedForOwner: boolean;
};

const Questions = ({ assetId, isLockedForOwner }: Props) => {
  const updateVideoMetadata = useVideoMetadataMutation(assetId);
  const { asset, resetValidationError } = useStore(
    useShallow((state) => ({
      asset: state.videoAssets[assetId],
      resetValidationError: state.resetVideoValidationError,
    })),
  );

  const resetFileValidation = () => {
    resetValidationError(assetId, "caption");
    resetValidationError(assetId, "transcript");
  };

  return (
    <div className="flex flex-col gap-y-space100">
      <Question
        msg={
          asset?.validationMessage?.find((v) => v.field === "isVideoWithAudio")
            ?.message
        }
      >
         <RadioButtonGroup
           disabled={isLockedForOwner}
           name={
             <Text tagName="h3" headingSize="xs">
               Does the video contain audio?
             </Text>
           }
           list={[
            {
              id: `onlyVideo-${assetId}`,
              label: "Only video",
              name: `videoAudio-${assetId}`,
              checked: asset?.isVideoWithAudio === false,
              onChange: async () => {
                await updateVideoMetadata.mutateAsync({
                  isVideoWithAudio: false,
                });
                resetValidationError(assetId, "isVideoWithAudio");
                resetFileValidation();
              },
            },
            {
              id: `videoWithAudio-${assetId}`,
              label: "Video with audio",
              name: `videoAudio-${assetId}`,
              checked: asset?.isVideoWithAudio === true,
              onChange: async () => {
                await updateVideoMetadata.mutateAsync({
                  isVideoWithAudio: true,
                });
                resetValidationError(assetId, "isVideoWithAudio");
                resetFileValidation();
              },
            },
          ]}
        />
      </Question>
      <Question
        msg={
          asset?.validationMessage?.find(
            (v) => v.field === "accessibilityPurpose",
          )?.message
        }
      >
         <RadioButtonGroup
           disabled={isLockedForOwner}
           name={
             <Text tagName="h3" headingSize="xs">
               Accessibility purpose
             </Text>
           }
           list={[
            {
              id: `decorative-${assetId}`,
              label: "Decorative",
              name: `videoType-${assetId}`,
              checked:
                asset?.accessibilityPurpose ===
                UpdateRelatedAssetMetadataDtoAccessibilityPurposeEnum.Decorative,
              onChange: async () => {
                await updateVideoMetadata.mutateAsync({
                  accessibilityPurpose:
                    UpdateRelatedAssetMetadataDtoAccessibilityPurposeEnum.Decorative,
                });
                resetValidationError(assetId, "accessibilityPurpose");
                resetFileValidation();
              },
            },
            {
              id: `informative-${assetId}`,
              label: "Informative",
              name: `videoType-${assetId}`,
              checked:
                asset?.accessibilityPurpose ===
                UpdateRelatedAssetMetadataDtoAccessibilityPurposeEnum.Informative,
              onChange: async () => {
                await updateVideoMetadata.mutateAsync({
                  accessibilityPurpose:
                    UpdateRelatedAssetMetadataDtoAccessibilityPurposeEnum.Informative,
                });
                resetValidationError(assetId, "accessibilityPurpose");
                resetFileValidation();
              },
            },
          ]}
        />
      </Question>
    </div>
  );
};

export default Questions;
